import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  IoCloudUploadOutline,
  IoLockClosedOutline,
  IoShieldCheckmark,
} from 'react-icons/io5';

import { TagInput } from './TagInput';
import { checkUrl } from '../api';

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex cursor-pointer select-none items-center">
    <span className="text-p-text mr-3 text-sm font-medium">{label}</span>
    <div className="relative">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div
        className={`h-8 w-14 rounded-full shadow-inner transition-colors ${checked ? 'bg-green-500' : 'bg-2'}`}
      ></div>
      <div
        className={`absolute left-1 top-1 h-6 w-6 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : ''}`}
      ></div>
    </div>
  </label>
);

export const McpOriginModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  userIdToken,
}) => {
  const [step, setStep] = useState(1);
  const [isChecking, setIsChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  // Form State
  const [sseUrl, setSseUrl] = useState('');
  const [originName, setOriginName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [requiredHeaders, setRequiredHeaders] = useState([]);

  const isEditMode = !!initialData;

  useEffect(() => {
    if (isEditMode) {
      setStep(2); // In edit mode, skip URL check
      setSseUrl(initialData.sse_url);
      setOriginName(initialData.origin_name);
      setDescription(initialData.description || '');
      setLogoUrl(initialData.logo_url || '');
      setIsGlobal(initialData.isGlobal || false);
      setRequiredHeaders(initialData.required_headers || []);
      // Simulate a check result for edit mode
      setCheckResult({
        oauth_available: initialData.auth_type === 'oauth2',
        origin: initialData.origin,
      });
    } else {
      // Reset form for create mode
      setStep(1);
      setSseUrl('');
      setOriginName('');
      setDescription('');
      setLogoUrl('');
      setIsGlobal(false);
      setRequiredHeaders([]);
      setCheckResult(null);
    }
  }, [isOpen, initialData, isEditMode]);

  const handleCheckUrl = async () => {
    if (!sseUrl || !sseUrl.startsWith('https') || !sseUrl.endsWith('/sse')) {
      toast.error('Please enter a valid HTTPS URL ending in /sse');
      return;
    }
    setIsChecking(true);
    try {
      const result = await checkUrl(userIdToken, sseUrl);
      setCheckResult(result);
      setOriginName(result.origin.split('//')[1].split('.')[1].toUpperCase()); // Guess a name
      setStep(2);
      toast.success('URL check successful!');
    } catch (error) {
      toast.error(`URL Check Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSave = () => {
    if (!originName) {
      toast.error('Origin Name is required.');
      return;
    }
    const authType = checkResult.oauth_available ? 'oauth2' : 'custom_header';
    const data = {
      origin: checkResult.origin,
      origin_name: originName,
      sse_url: sseUrl,
      auth_type: authType,
      description,
      logo_url: logoUrl,
      isGlobal,
      required_headers: authType === 'custom_header' ? requiredHeaders : [],
    };
    onSave(data, isEditMode ? initialData.id : null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-1 text-p-text flex max-h-[90vh] w-full max-w-2xl transform flex-col rounded-2xl shadow-xl transition-all">
        <div className="border-line border-b p-6">
          <h2 className="text-2xl font-bold">
            {isEditMode ? 'Edit MCP Origin' : 'Add New MCP Origin'}
          </h2>
          <p className="text-s-text">
            {isEditMode
              ? `Editing: ${initialData.origin_name}`
              : 'Create a new template for users to connect to.'}
          </p>
        </div>

        <div className="space-y-6 overflow-y-auto p-6">
          {/* --- Step 1: URL Check --- */}
          <div className={step === 2 && isEditMode ? 'opacity-50' : ''}>
            <label htmlFor="sseUrl" className="text-sm font-medium">
              MCP SSE URL
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id="sseUrl"
                type="text"
                value={sseUrl}
                onChange={(e) => setSseUrl(e.target.value)}
                placeholder="https://mcp.example.com/sse"
                disabled={step === 2 || isChecking}
                className="bg-2 border-line w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#5652CF] disabled:opacity-50"
              />
              {step === 1 && (
                <button
                  onClick={handleCheckUrl}
                  disabled={isChecking || !sseUrl}
                  className="whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isChecking ? 'Checking...' : 'Check URL'}
                </button>
              )}
            </div>
          </div>

          {/* --- Step 2: Details Form --- */}
          {step === 2 && checkResult && (
            <div className="border-line animate-fade-in space-y-6 border-t pt-6">
              {/* ... Form fields for name, description, logo, etc. ... */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="originName" className="text-sm font-medium">
                    Origin Name
                  </label>
                  <input
                    id="originName"
                    type="text"
                    value={originName}
                    onChange={(e) => setOriginName(e.target.value)}
                    className="bg-2 border-line mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                  />
                </div>
                <div>
                  <label htmlFor="logoUrl" className="text-sm font-medium">
                    Logo URL
                  </label>
                  <input
                    id="logoUrl"
                    type="text"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://.../logo.svg"
                    className="bg-2 border-line mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                  />
                </div>
              </div>
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Logo Preview"
                  className="bg-2 h-16 w-16 rounded-lg object-contain p-1"
                  onError={(e) => (e.target.style.display = 'none')}
                  onLoad={(e) => (e.target.style.display = 'block')}
                />
              )}
              <div>
                <label htmlFor="description" className="text-sm font-medium">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="3"
                  className="bg-2 border-line mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                />
              </div>

              {/* --- Auth Type & Required Headers --- */}
              <div className="bg-2 rounded-lg p-4">
                {checkResult.oauth_available ? (
                  <p className="flex items-center gap-2 font-medium text-green-400">
                    <IoShieldCheckmark /> OAuth 2.0 is available for this
                    origin.
                  </p>
                ) : (
                  <div>
                    <p className="flex items-center gap-2 font-medium text-yellow-400">
                      <IoLockClosedOutline /> Custom Header authentication will
                      be used.
                    </p>
                    <div className="mt-4">
                      <label
                        htmlFor="requiredHeaders"
                        className="text-sm font-medium"
                      >
                        Required Auth Headers
                      </label>
                      <p className="text-s-text mb-2 text-xs">
                        Add the header keys users must provide (e.g.,
                        'Authorization').
                      </p>
                      <TagInput
                        tags={requiredHeaders}
                        setTags={setRequiredHeaders}
                      />
                    </div>
                  </div>
                )}
              </div>
              <Toggle
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                label="Make this a Global Connection?"
              />
            </div>
          )}
        </div>

        <div className="bg-2 flex justify-end gap-4 rounded-b-2xl p-6">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-600 px-4 py-2 font-semibold text-white hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={step === 1}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            <IoCloudUploadOutline />{' '}
            {isEditMode ? 'Save Changes' : 'Create Origin'}
          </button>
        </div>
      </div>
    </div>
  );
};
